import { Injectable, computed, inject, signal, OnDestroy } from '@angular/core';
import { TenantConfiguration } from '@shared/types/clients.types';
import { ClientsApiService } from '@services/clients-api.service';
import { UserAuthorizationService } from '@services/user-authorization.service';
import { clientMiscPermissions } from '@shared/strings/common.strings';

/**
 * Global store for tenant configuration and permissions
 * This service is the SINGLE SOURCE OF TRUTH for all permission checks
 *
 * Key Features:
 * - Manages tenant permissions from API
 * - Automatically handles Super Admin bypass (super admins have all permissions)
 * - Provides computed signals for common permission checks
 * - Signal-based reactivity for automatic UI updates
 *
 * The configuration is loaded from API on login and page refresh,
 * stored in-memory via Angular signals (no localStorage needed)
 */
@Injectable({
  providedIn: 'root',
})
export class TenantConfigurationService implements OnDestroy {
  private clientsApiService = inject(ClientsApiService);
  private userAuthorizationService = inject(UserAuthorizationService);

  // Tenant configuration state (in-memory global store)
  private tenantConfigSignal = signal<TenantConfiguration | null>(null);
  private isLoadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Expose read-only signals
  readonly tenantConfig = this.tenantConfigSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // Computed signals for common permission checks
  // readonly canExport = computed(() =>
  //   this.hasPermission(clientMiscPermissions.exports.id),
  // );

  // readonly canAccessAnalytics = computed(() =>
  //   this.hasPermission(clientMiscPermissions.analytics.id),
  // );

  // readonly canManageUsers = computed(() =>
  //   this.hasPermission(clientMiscPermissions.userManagement.id),
  // );

  // Computed signals for tenant info
  // readonly organizationName = computed(
  //   () => this.tenantConfigSignal()?.organizationName ?? '',
  // );

  readonly tenantName = computed(
    () => this.tenantConfigSignal()?.tenantName ?? '',
  );

  readonly permissions = computed(() => this.tenantConfigSignal()?.misc ?? []);

  private getTenantConfigurationSubscription: any;

  ngOnDestroy() {
    this.getTenantConfigurationSubscription?.unsubscribe();
  }

  /**
   * Load tenant configuration from the API (fire-and-forget)
   * This method handles everything internally - no need to subscribe
   * @param tenantId - The tenant ID to load configuration for
   */
  loadTenantConfiguration(tenantId: string): void {
    if (!tenantId) {
      this.errorSignal.set('Tenant ID is required');
      return;
    }

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    this.getTenantConfigurationSubscription = this.clientsApiService
      .getTenantConfiguration(tenantId)
      .subscribe({
        next: config => {
          this.getTenantConfigurationSubscription = undefined;
          this.tenantConfigSignal.set(config);
          this.isLoadingSignal.set(false);
        },
        error: error => {
          this.getTenantConfigurationSubscription = undefined;
          this.errorSignal.set('Failed to load tenant configuration');
          this.isLoadingSignal.set(false);
        },
      });
  }

  /**
   * Check if user has a specific permission
   * Super Admins automatically have ALL permissions
   * @param permissionId - The permission ID to check
   * @returns true if user has the permission (or is super admin)
   */
  hasPermission(permissionId: string): boolean {
    // Super Admins have all permissions
    if (this.userAuthorizationService.isSuperAdmin) {
      return true;
    }

    return this.permissionCheck(permissionId);
  }

  /**
   * Check if user has ALL of the specified permissions
   * Super Admins automatically have ALL permissions
   * @param permissionIds - Array of permission IDs to check
   * @returns true if user has all permissions (or is super admin)
   */
  // hasAllPermissions(permissionIds: string[]): boolean {
  //   // Super Admins have all permissions
  //   if (this.userAuthorizationService.isSuperAdmin) {
  //     return true;
  //   }
  //   return permissionIds.every(id => this.hasPermission(id));
  // }

  /**
   * Check if user has ANY of the specified permissions
   * Super Admins automatically have ALL permissions
   * @param permissionIds - Array of permission IDs to check
   * @returns true if user has at least one permission (or is super admin)
   */
  // hasAnyPermission(permissionIds: string[]): boolean {
  //   // Super Admins have all permissions
  //   if (this.userAuthorizationService.isSuperAdmin) {
  //     return true;
  //   }
  //   return permissionIds.some(id => this.hasPermission(id));
  // }

  /**
   * Check if user has access to a specific route
   * Uses feature configuration to determine required permissions
   * Super Admins automatically have access to all routes
   * @param routePermission - The route permission ID to check.
   * @returns true if user has access to the route
   */
  hasRouteAccess(routePermission: string | undefined): boolean {
    if (!routePermission) {
      return true;
    }

    // Super Admins have access to everything
    if (this.userAuthorizationService.isSuperAdmin) {
      return true;
    }

    return this.permissionCheck(routePermission);
  }

  /**
   * Clear the tenant configuration
   * Call this when switching tenants or logging out
   */
  clearConfiguration(): void {
    this.tenantConfigSignal.set(null);
    this.errorSignal.set(null);
    this.isLoadingSignal.set(false);
  }

  /**
   * Manually set tenant configuration
   * Useful for testing or when configuration is provided from another source
   * @param config - The tenant configuration to set
   */
  setConfiguration(config: TenantConfiguration): void {
    this.tenantConfigSignal.set(config);
    this.errorSignal.set(null);
  }

  permissionCheck(permissionId: string): boolean {
    const config = this.tenantConfigSignal();
    if (!config?.misc) {
      return false;
    }

    switch (permissionId) {
      case clientMiscPermissions.exports.id:
        return !!config?.misc.allowDataExports?.id;
      case clientMiscPermissions.userManagement.id:
        return !!config?.misc.allowUserManagement?.id;
      case clientMiscPermissions.analytics.id:
        return !!(
          config?.misc.emailAnalytics?.id ||
          config?.misc.emailDashboard?.id ||
          config?.misc.websiteAnalytics?.id
        );
      case clientMiscPermissions.emailAnalytics.id:
        return !!config?.misc.emailAnalytics?.id;
      case clientMiscPermissions.emailDashboard.id:
        return !!config?.misc.emailDashboard?.id;
      case clientMiscPermissions.websiteAnalytics.id:
        return !!config?.misc.websiteAnalytics?.id;
      default:
        return false;
    }
  }
}
